import React from "react";
import { motion } from "framer-motion";

/**
 * The typed words, read right to left, with each letter lighting up as it's
 * played — the same read-along the demo gives you, sitting above the harp.
 * Letters are keyed "wordIdx:charIdx", which is what the play sequence carries.
 */
const PALETTES = {
  dark: { panel: "#0C0826", border: "#1A0F3C", hover: "#3A2158", idle: "#6F6486", played: "#F2D589", active: "#FCA07A" },
  light: { panel: "#FFFBF2", border: "#EBE3D5", hover: "#D6CBB8", idle: "#8C8198", played: "#B8862B", active: "#E86F4A" },
};

export default function LetterStrip({ sequence, playedKeys, activeKey, hint, onWordClick, theme = "dark", testId = "harp-letter-strip" }) {
  const c = PALETTES[theme] || PALETTES.dark;
  if (!sequence || sequence.length === 0) {
    return (
      <div
        className="rounded-md border px-4 py-7 text-center"
        style={{ borderColor: c.border, backgroundColor: c.panel }}
        data-testid={testId}
      >
        <p className="text-xs" style={{ color: c.idle, fontFamily: "'Courier Prime', monospace" }}>
          {hint || "Pluck a string, or type a word above."}
        </p>
      </div>
    );
  }

  const words = [];
  sequence.forEach((e) => {
    const last = words[words.length - 1];
    if (last && last.wordIdx === e.wordIdx) last.entries.push(e);
    else words.push({ wordIdx: e.wordIdx, entries: [e] });
  });

  return (
    <div
      dir="rtl"
      className="rounded-md border px-4 py-5 sm:px-6 flex flex-wrap items-center justify-center gap-x-7 gap-y-4"
      style={{ borderColor: c.border, backgroundColor: c.panel }}
      data-testid={testId}
    >
      {words.map((w) => (
        <button
          key={w.wordIdx}
          type="button"
          onClick={onWordClick ? () => onWordClick(w.wordIdx) : undefined}
          disabled={!onWordClick}
          title={onWordClick ? "Play this word on its own" : undefined}
          className="flex items-center gap-x-1.5 rounded-md px-2 py-1 transition-colors disabled:cursor-default"
          style={{
            border: "1px solid transparent",
            backgroundColor: "transparent",
            cursor: onWordClick ? "pointer" : "default",
          }}
          onMouseEnter={(e) => {
            if (onWordClick) e.currentTarget.style.borderColor = c.hover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "transparent";
          }}
          data-testid={`strip-word-${w.wordIdx}`}
        >
          {w.entries.map((e) => {
            const key = `${e.wordIdx}:${e.charIdx}`;
            const isActive = key === activeKey;
            const isPlayed = playedKeys?.has(key);
            return (
              <span key={key} className="relative inline-block">
                <motion.span
                  className="block"
                  data-testid={`strip-letter-${key}`}
                  data-state={isActive ? "active" : isPlayed ? "played" : "idle"}
                  animate={{
                    color: isActive ? c.active : isPlayed ? c.played : c.idle,
                    scale: isActive ? 1.16 : 1,
                  }}
                  transition={{ duration: 0.22 }}
                  style={{
                    fontFamily: "'Frank Ruhl Libre', 'Cormorant Garamond', serif",
                    fontSize: "clamp(1.5rem, 3.2vw, 2.3rem)",
                    lineHeight: 1.1,
                  }}
                >
                  {e.letter}
                </motion.span>
                {isActive && (
                  <motion.span
                    aria-hidden="true"
                    className="absolute left-1/2 top-1/2 rounded-full pointer-events-none"
                    style={{
                      width: 16,
                      height: 16,
                      marginLeft: -8,
                      marginTop: -8,
                      border: `1px solid ${c.active}`,
                    }}
                    initial={{ opacity: 0.7, scale: 0.5 }}
                    animate={{ opacity: 0, scale: 3.2 }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                  />
                )}
              </span>
            );
          })}
        </button>
      ))}
    </div>
  );
}
